import React from 'react';

import { useHistory, useParams, useRouteMatch } from 'react-router';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '../';
import {
	faBinoculars,
	faBullseyeArrow,
	faChevronLeft,
	faChevronRight,
	faClone,
	faEdit,
	faFishBones,
	faGaugeHigh,
	faGear,
	faHome,
	faHouseFlag,
	faPlus,
	faRadar,
	faShieldExclamation,
	faSplit
} from '@fortawesome/pro-light-svg-icons';
import ProjectModal from 'components/Modal/ProjectModal';
import ScenarioEditionModal from 'components/Modal/ScenarioEditionModal';
import ScenarioElaborationModal from 'components/Modal/ScenarioElaborationModal';
// AIScenarioGenerationModal is loaded dynamically inside the handler below
// so the prod bundle (where AI_GENERATION_ENABLED is false) never carries
// the module — Vite/rolldown couldn't tree-shake the static import because
// the modal pulls in a `.sass` side-effect that defeats DCE.
import { AI_GENERATION_ENABLED } from 'config/featureFlags';

import { Button, Dropdown, Flex, Modal, Skeleton, Tooltip, Typography } from 'antd';
import { ReadOnlyBadge } from 'components/ReadOnlyBadge';

import ConfirmModal from 'components/Modal/ConfirmModal';
import { keys, sortBy } from 'lodash';

import { useProjectController } from 'hooks/useProjectController';
import { useScenarioController } from 'hooks/useScenarioController';

import './ProjectNavigation.sass';
import PromoteAsBaselineModal from 'components/Modal/PromoteAsBaselineModal';
import UIContext from 'components/Utils/UIContext';

function ProjectNavigation(props: any) {
	let history = useHistory();
	const params: any = useParams();
	// Lecture seule : masque les affordances d'édition (edit projet/scénario,
	// création/clone/promotion de scénario) pour un lecteur.
	const { canEdit, isReadOnly } = React.useContext(UIContext) as any;
	const { scenarios = [] } = props;
	const { t, i18n } = useTranslation();

	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const [showVision, setShowVision] = React.useState(false);
	const [editedScenario, setEditedScenario] = React.useState<any | null>(
		null
	);

	const sortedScenarios = sortBy(scenarios, 'name');

	const { createScenario, updateScenario, deleteScenario, promoteScenarioAsBaseline, launchScenarioElaboration } = useScenarioController();

	const { updateProject } = useProjectController();


	const handleAskToCreateScenario = async (referenceScenario: any) => {
		props.pushModal(
			<ScenarioEditionModal
				projectId={params.id}
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					// V2 — AI scenario generation flow: create scenario first, then open
					// the AI modal which streams progress phases and applies impacts.
					if (AI_GENERATION_ENABLED && values?.aiGenerate && values?.hypothesis && values?.horizon) {
						const aiResult = await createScenario(
							params.id,
							props,
							referenceScenario,
							{ name: values.name, description: values.description, impacts: values.impacts },
							() => {}
						);
						if (props.refetch) await props.refetch();
						if (!aiResult?.scenarioId) return;
						// Env-literal-gated dynamic import: rolldown DCEs the
						// whole branch on prod, so the AI chunk never ships.
						const { default: AIScenarioGenerationModal } = await import(
							'components/Modal/AIScenarioGenerationModal'
						);
						props.replaceModal(
							<AIScenarioGenerationModal
								visible={true}
								projectId={params.id}
								scenarioId={aiResult.scenarioId}
								scenarioName={values.name}
								hypothesis={values.hypothesis}
								horizon={values.horizon}
								scenario={{ id: aiResult.scenarioId, name: values.name }}
								stakeholders={props.stakeholders || []}
								stakes={props.stakes || []}
								risks={props.risks || []}
								indicators={props.indicators || []}
								scenarios={props.scenarios || []}
								fishboneBranchs={props.fishboneBranchs || []}
								likelihoods={props.likelihoods || []}
								impacts={props.impacts || []}
								onClose={() => {
									props.popModal();
								}}
								onComplete={async (createdScenarioId: string) => {
									if (props.refetch) await props.refetch();
									props.popModal();
									if (createdScenarioId) {
										props.onGoToScenario(createdScenarioId);
									}
								}}
							/>
						);
						return;
					}

					const result = await createScenario(
						params.id,
						props,
						referenceScenario,
						values,
						() => {}
					);
					if (props.refetch) await props.refetch();

					// Si le scénario nécessite une élaboration IA, afficher le modal de progression
					if (result?.needsElaboration && result.scenarioId && result.simulationQuery) {
						console.log('🟢 [ProjectNav] Affichage du modal de progression...');

						// Créer un objet scénario temporaire pour le nouveau scénario
						const newScenario = { id: result.scenarioId, name: values.name };

						// Remplacer le modal de création par le modal de progression
						props.replaceModal(
							<ScenarioElaborationModal
								visible={true}
								projectId={params.id}
								scenarioId={result.scenarioId}
								scenarioName={values.name}
								query={result.simulationQuery}
								scenario={newScenario}
								stakeholders={props.stakeholders || []}
								stakes={props.stakes || []}
								risks={props.risks || []}
								indicators={props.indicators || []}
								scenarios={props.scenarios || []}
								likelihoods={props.likelihoods || []}
								impacts={props.impacts || []}
								onComplete={() => {
									console.log('🟢 [ProjectNav] Élaboration terminée');
								}}
								onClose={() => {
									console.log('🟢 [ProjectNav] Fermeture du modal de progression');
									props.popModal();
									// Naviguer vers le nouveau scénario par son ID (sera trouvé dans la liste triée)
									props.onGoToScenario(result.scenarioId);
								}}
							/>
						);

						// Lancer l'élaboration après un court délai
						setTimeout(() => {
							console.log('🟢 [ProjectNav] Lancement de l\'élaboration...');
							launchScenarioElaboration(
								params.id,
								result.scenarioId,
								result.simulationQuery!,
								i18n.language
							);
						}, 500);
					} else {
						console.log('🔴 [ProjectNav] Pas d\'élaboration, fermeture du modal');
						// Fermer le modal et naviguer vers le nouveau scénario par son ID
						props.popModal();
						props.onGoToScenario(result?.scenarioId);
					}
				}}
				id={false}
				values={{
					name:
						referenceScenario && referenceScenario.name !== null
							? referenceScenario.name + ' - Copy'
							: '',
					description:
						referenceScenario &&
							referenceScenario.description !== null
							? referenceScenario.description
							: '',
					impacts:
						referenceScenario && referenceScenario.impacts !== null
							? referenceScenario.impacts
							: ''
				}}
			></ScenarioEditionModal>
		);
	};

	const askDeleteConfirmation = async (id?: string) => {
		props.pushModal(
			<ConfirmModal
				title={t('models.scenario.messages.delete_confirmation')}
				t={t}
				onConfirm={() => handleDelete(id)}
				message={t(
					'models.scenario.messages.delete_confirmation_explanation'
				)}
			></ConfirmModal>
		);
	};

	const askPromoteAsBaselineConfirmation = async (scenario) => {

		props.pushModal(
			<PromoteAsBaselineModal
				t={t}
				onSave={async (values) => {
					await promoteScenarioAsBaseline(props, scenario, values.keepCurrentBaseline, values.name, values.keepScenario);
					if (props.refetch) await props.refetch();
					setEditedScenario(null);
					props.onGoToScenario(null);
				}}
				onCancel={() => {
					setEditedScenario(null);
				}}
				message={t(
					'models.scenario.messages.promote_as_baseline.explanation'
				)}
				values={{
					keepCurrentBaseline: true,
					keepScenario: true
				}}
				scenario={scenario}
			></PromoteAsBaselineModal>
		);
	};

	// Delete
	const handleDelete = async (id: string | undefined) => {

		if (id == null) {
			return;
		}
		props.onGoToPreviousScenario();
		await deleteScenario(params.id, id);
	};

	const fishboneIsActive = useRouteMatch(
		'/projects/' + params?.id + '/fishbone'
	);
	const stakesIsActive = useRouteMatch(
		'/projects/' + params?.id + '/' + t('pages.stakes.url')
	);
	const constraintsIsActive = useRouteMatch(
		'/projects/' + params?.id + '/' + t('pages.indicators.url')
	);
	const scenariosIsActive = useRouteMatch(
		'/projects/' + params?.id + '/scenarios'
	);
	const risksMatch = useRouteMatch('/projects/' + params?.id + '/risks');
	const opportunitiesMatch = useRouteMatch(
		'/projects/' + params?.id + '/opportunities'
	);
	const summaryMatch = useRouteMatch('/projects/' + params?.id + '/summary');
	const risksIsActive = risksMatch || opportunitiesMatch || summaryMatch;
	const watchIsActive = useRouteMatch(
		'/projects/' + params?.id + '/' + t('pages.watch.url')
	);
	const navigatorIsActive =
		useRouteMatch('/projects/' + params?.id) &&
		!scenariosIsActive &&
		!fishboneIsActive &&
		!stakesIsActive &&
		!constraintsIsActive &&
		!watchIsActive &&
		!risksIsActive;


	if (props.loading) return <div className="ProjectNavigation noselection">
		<Flex align="center" justify="center" style={{ width: '100%' }}>
			<Skeleton style={{ width: "25%" }} className="mt-05 mr-1" active={true} title paragraph={false}></Skeleton>
			<Skeleton style={{ width: "60%" }} className="mt-05 mr-1" active={true} title paragraph={false}></Skeleton>
			<Skeleton className="mt-05" active={true} title paragraph={false}></Skeleton>
		</Flex>
	</div>

	const scenarioItems = [{
		key: 0,
		index: 0,
		label: t("models.scenario.baseline"),
		disabled: props.scenarioIndex === null
	}].concat(sortedScenarios.map((s: any, index: number) => {
		return {
			key: index + 1,
			index: index + 1,
			label: s.name,
			description: s.description,
			disabled: props.scenarioIndex === index
		};
	}))

	return (
		<div className="ProjectNavigation noselection">

			<Tooltip title={t("pages.projects.actions.back_to_this")}>
				<Button className="mr-1" shape="circle" onClick={() => {
					props.setExpanded(false);
					history.push('/');
				}}>
					<FontAwesomeIcon icon={faHome} />
				</Button>
			</Tooltip>

			<Flex className="title nowrap" align="center">
				<span
					className="cursor-pointer"
					style={{ textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
					onClick={() => setShowVision(true)}
				>
					{props.project?.name}
				</span>{' '}

				{false && <Tooltip title={t('messages.situation_analysis.title')}>
					<Button className="ml-1 mini" onClick={() => props.onOpenSituationAnalysis && props.onOpenSituationAnalysis()}>
						{t('messages.situation_analysis.title')}
					</Button>
				</Tooltip>}

			</Flex>
			<div className="separator"></div>

			{props.displayStakes && (
				<Tooltip title={t('pages.stakes.title')}>
					<a
						className={[
							stakesIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-stakes"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.stakes.url'));
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.stakes.url'));
						}}
					>
						<FontAwesomeIcon icon={faBullseyeArrow} />
					</a>
				</Tooltip>
			)}

			{props.displayRisks && (
				<Tooltip title={t('pages.risks.title')}>
					<a
						className={[
							risksIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-risks"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/risks');
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/risks');
						}}
					>
						<FontAwesomeIcon icon={faShieldExclamation} />
					</a>
				</Tooltip>
			)}

			{(props.displayAdvancedScenarios || props.displayRisks || props.displayStakeholders) && (
				<Tooltip title={t('pages.navigator.title')}>
					<a
						className={[
							navigatorIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-navigator"
						onTouchStart={() => {
							history.push('/projects/' + params?.id);
						}}
						onClick={() => {
							history.push('/projects/' + params?.id);
						}}
					>
						<FontAwesomeIcon icon={faRadar} />
					</a>
				</Tooltip>
			)}

			{(props.displayAdvancedScenarios) && (
				<Tooltip title={t('pages.scenarios.title')}>
					<a
						className={[
							scenariosIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-scenarios"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/scenarios');
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/scenarios');
						}}
					>
						<FontAwesomeIcon icon={faSplit} />
					</a>
				</Tooltip>
			)}

			{props.displayFishbone && (
				<Tooltip title={t('pages.fishbone.title')}>
					<a
						className={[
							fishboneIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-fishbone"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/fishbone');
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/fishbone');
						}}
					>
						<FontAwesomeIcon icon={faFishBones} />
					</a>
				</Tooltip>
			)}

			{props.displayIndicators && (
				<Tooltip title={t('pages.indicators.title')}>
					<a
						className={[
							constraintsIsActive ? 'selected' : '',
							'nav-icon',
							'mr-2'
						].join(' ')}
							data-testid="nav-indicators"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.indicators.url'));
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.indicators.url'));
						}}
					>
						<FontAwesomeIcon icon={faGaugeHigh} />
					</a>
				</Tooltip>
			)}
			{props.displayWatch && (
				<Tooltip title={t('pages.watch.title')}>
					<a
						className={[
							watchIsActive ? 'selected' : '',
							'nav-icon',
							''
						].join(' ')}
							data-testid="nav-watch"
						onTouchStart={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.watch.url'));
						}}
						onClick={() => {
							history.push('/projects/' + params?.id + '/' + t('pages.watch.url'));
						}}
					>
						<FontAwesomeIcon icon={faBinoculars} />
					</a>
				</Tooltip>
			)}
			{props.displayAdvancedScenarios && !watchIsActive && (
				<div className="separator"></div>
			)}

			{!watchIsActive && <div className="scenario">
				<div className="navigation mr-2">

					<Tooltip title={props.scenarioIndex + 2 > 2 ? t('models.scenario.actions.previous') : t('models.scenario.actions.previous_baseline')}>
						<Button
							shape="circle"
							className={`mini mr-05`}
							onClick={props.onGoToPreviousScenario}
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</Button>
					</Tooltip>

					{props.scenarioIndex !== null
						? props.scenarioIndex + 2
						: 1}{' '}
					/
					{props.scenarioCount !== undefined
						? props.scenarioCount + 1
						: 1}

					<Tooltip title={t('models.scenario.actions.next')}>
						<Button
							shape="circle"
							className="mini ml-05"
							onClick={props.onGoToNextScenario}
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</Button>
					</Tooltip>

				</div>


				<Tooltip title={t("models.scenario.messages.switch_scenario")}>
					<div className="name">

						<Dropdown menu={{
							items: scenarioItems,
							selectable: true,
							defaultSelectedKeys: props.scenarioIndex || 0,
							onClick: (item: any) => {
								props.onGoToScenario(parseInt(item.key) > 0 ? parseInt(item.key) - 1 : null);
							}

						}}
							trigger={['click']}>
							<div
								className="cursor-pointer"
								style={{ display: 'flex', alignItems: 'center', maxWidth: 360 }}
							>
								<span
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										minWidth: 0
									}}
								>
									{props.scenario?.name || t("models.scenario.baseline")}
								</span>
								<small className="ml-1" style={{ flexShrink: 0 }}>
									(
									{props.scenarioIndex === null
										? 'B'
										: 'S' + (props.scenarioIndex + 1)}
									)
								</small>

							</div>
						</Dropdown>


					</div></Tooltip>



				{canEdit && <div className="ml-2 mr-2 actions">
					{props.scenarioIndex !== null && (
						<Tooltip title={t('models.scenario.actions.clone')}>
							<Button
								shape="circle"
								className="createScenario mini"
								onClick={() => {
									handleAskToCreateScenario(
										props.scenario
									);
								}}
							>
								<FontAwesomeIcon
									icon={faClone}
								></FontAwesomeIcon>
							</Button>{' '}
						</Tooltip>
					)}

					{props.scenarioIndex === null && (
						<Tooltip
							title={t('models.scenario.actions.create_new')}
						>
							<Button className="createScenario mini" shape="circle"
								onClick={() => handleAskToCreateScenario()}
							>
								<FontAwesomeIcon
									icon={faPlus}
								></FontAwesomeIcon>
							</Button>{' '}
						</Tooltip>
					)}

					<Tooltip title={t('models.scenario.actions.edit')}>
						<Button
							shape="circle"
							className="mini mr-05 ml-05"
							onClick={() => {
								setEditedScenario(props.scenario || { id: null });
							}}
						>
							<FontAwesomeIcon
								icon={faEdit}
							></FontAwesomeIcon>
						</Button>
					</Tooltip>

					{props.scenarioIndex !== null && (
						<Tooltip title={t('models.scenario.messages.promote_as_baseline.title')}>
							<Button
								shape="circle"
								className="mini"
								onClick={() => {
									askPromoteAsBaselineConfirmation(props.scenario);
									setEditedScenario(null);
								}}
							>
								<FontAwesomeIcon
									icon={faHouseFlag}
								></FontAwesomeIcon>
							</Button>{' '}
						</Tooltip>
					)}
				</div>}
			</div>}

			{canEdit && (
				<div style={{ marginLeft: 'auto' }} className="mr-1">
					<Tooltip title={t("models.project.actions.edit")}>
						<Button data-testid="project-settings-btn" className="mini" shape="circle" onClick={() => setEditedItem(props.project)}>
							<FontAwesomeIcon icon={faGear} />
						</Button>
					</Tooltip>
				</div>
			)}

			{isReadOnly && (
				<div style={{ marginLeft: canEdit ? undefined : 'auto' }} className="mr-2">
					<ReadOnlyBadge />
				</div>
			)}

			<Modal
				open={showVision}
				onCancel={() => setShowVision(false)}
				footer={canEdit ? (
					<Button type="primary" onClick={() => { setShowVision(false); setEditedItem(props.project); }}>
						<FontAwesomeIcon icon={faEdit} className="mr-1" /> {t('models.project.actions.edit')}
					</Button>
				) : null}
				title={props.project?.name}
				width={560}
			>
				{[
					{ label: t('models.project.fields.description'), value: props.project?.description },
					{ label: t('models.project.fields.vision'), value: props.project?.vision },
					{ label: t('models.project.fields.description_long'), value: props.project?.descriptionLong },
				].map(({ label, value }) => value ? (
					<div key={label} style={{ marginBottom: '1rem' }}>
						<Typography.Text strong>{label}</Typography.Text>
						<div style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap' }}>{value}</div>
					</div>
				) : null)}
			</Modal>

			{editedItem && (
				<ProjectModal
					onHide={() => {
						setEditedItem(null);
					}}
					onSave={(values: Object) => {
						props.onUpdate(editedItem.id, values);
						setEditedItem(null);
					}}
					id={editedItem?.id}
					key={editedItem?.id}
					values={{
						name: editedItem?.name,
						reference: editedItem?.reference,
						description: editedItem?.description,
						descriptionLong: editedItem?.descriptionLong,
						tags: editedItem?.tags,
						riskCategories: editedItem?.riskCategories,
						indicatorCategories: editedItem?.indicatorCategories,
						vision: editedItem?.vision,
						likelihoods: editedItem?.likelihoods,
						impacts: editedItem?.impacts,
						type: editedItem?.type,
					}}
					authorizedTypes={props.authorizedProjectTypes}
				></ProjectModal>
			)}
			{editedScenario && (
				<ScenarioEditionModal
					onHide={() => {
						setEditedScenario(null);
					}}
					onDelete={() => {
						askDeleteConfirmation(editedScenario.id);
						setEditedScenario(null);
					}}
					onSave={(values: Object) => {
						let variables = {}
						if (values.description !== undefined) variables['baselineScenarioDescription'] = values.description
						if (values.impacts !== undefined) variables['baselineScenarioImpacts'] = values.impacts

						if (!editedScenario.id) {
							if (keys(variables).length > 0) {
								updateProject(params.id, variables);
							}
						} else {
							updateScenario(editedScenario.id, scenarios, values);
						}
						setEditedScenario(null);
					}}
					id={editedScenario?.id}
					values={{
						name: editedScenario?.id ? editedScenario?.name : t("models.scenario.baseline"),
						description: editedScenario?.id ? editedScenario?.description || '' : props?.baselineScenarioDescription || '',
						impacts: editedScenario?.id ? editedScenario?.impacts || '' : props?.baselineScenarioImpacts || ''
					}}
				></ScenarioEditionModal>
			)}
		</div>
	);
}

export default ProjectNavigation;
